import { useState, useEffect } from 'react';
import { HeroConfig, DEFAULT_HERO_VISUAL_CONFIG } from '../types/hero';
import { DEFAULT_HEROES } from '../mocks/heroes';
import { supabase } from '../lib/supabase';

/**
 * Normaliza valores numéricos opcionais para manter a tipagem correta
 * e evitar string em sliders/ranges no admin.
 */
function clampNumber(value: unknown, min: number, max: number, fallback: number): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(parsed)) return fallback;
  return Math.min(max, Math.max(min, parsed));
}

/**
 * Garante compatibilidade entre registros antigos e novos campos visuais.
 */
function withVisualDefaults(hero: Partial<HeroConfig>): HeroConfig {
  return {
    ...DEFAULT_HERO_VISUAL_CONFIG,
    ...hero,
    hero_background_opacity: clampNumber(
      hero.hero_background_opacity,
      0,
      1,
      DEFAULT_HERO_VISUAL_CONFIG.hero_background_opacity
    ),
    hero_overlay_opacity: clampNumber(
      hero.hero_overlay_opacity,
      0,
      1,
      DEFAULT_HERO_VISUAL_CONFIG.hero_overlay_opacity
    ),
    hero_gradient_opacity: clampNumber(
      hero.hero_gradient_opacity,
      0,
      1,
      DEFAULT_HERO_VISUAL_CONFIG.hero_gradient_opacity
    ),
    stats_block_opacity: clampNumber(
      hero.stats_block_opacity,
      0,
      1,
      DEFAULT_HERO_VISUAL_CONFIG.stats_block_opacity
    ),
    stats_block_blur: clampNumber(
      hero.stats_block_blur,
      0,
      30,
      DEFAULT_HERO_VISUAL_CONFIG.stats_block_blur
    ),
    badge_shadow:
      typeof hero.badge_shadow === 'boolean'
        ? hero.badge_shadow
        : DEFAULT_HERO_VISUAL_CONFIG.badge_shadow,
    stats_block_shadow:
      typeof hero.stats_block_shadow === 'boolean'
        ? hero.stats_block_shadow
        : DEFAULT_HERO_VISUAL_CONFIG.stats_block_shadow,
  } as HeroConfig;
}

/**
 * Converte o payload do banco para o formato usado no front.
 */
function mapRowToHero(row: any): HeroConfig {
  return withVisualDefaults({
    id: row.hero_id,
    badge: row.badge,
    title: row.title,
    subtitle: row.subtitle,
    buttons: row.buttons,
    stats: row.stats,
    backgroundImage: row.background_image,
    isActive: row.is_active,
    createdAt: row.created_at || row.updated_at,
    updatedAt: row.updated_at,

    hero_background_mode: row.hero_background_mode,

    hero_background_color: row.hero_background_color,
    hero_background_opacity: row.hero_background_opacity,
    hero_background_gradient: row.hero_background_gradient,

    hero_solid_color: row.hero_solid_color,

    hero_overlay_color: row.hero_overlay_color,
    hero_overlay_opacity: row.hero_overlay_opacity,

    hero_gradient_from: row.hero_gradient_from,
    hero_gradient_to: row.hero_gradient_to,
    hero_gradient_direction: row.hero_gradient_direction,
    hero_gradient_opacity: row.hero_gradient_opacity,

    badge_background_color: row.badge_background_color,
    badge_text_color: row.badge_text_color,
    badge_border_color: row.badge_border_color,
    badge_shadow: row.badge_shadow,

    button_primary_color: row.button_primary_color,
    button_primary_text_color: row.button_primary_text_color,
    button_primary_border_color: row.button_primary_border_color,
    button_primary_hover: row.button_primary_hover,
    button_primary_gradient: row.button_primary_gradient,

    button_secondary_color: row.button_secondary_color,
    button_secondary_text_color: row.button_secondary_text_color,
    button_secondary_border_color: row.button_secondary_border_color,
    button_secondary_hover: row.button_secondary_hover,
    button_secondary_gradient: row.button_secondary_gradient,

    stats_block_background: row.stats_block_background,
    stats_block_opacity: row.stats_block_opacity,
    stats_block_border: row.stats_block_border,
    stats_block_blur: row.stats_block_blur,
    stats_block_text_color: row.stats_block_text_color,
    stats_block_icon_color: row.stats_block_icon_color,
    stats_block_shadow: row.stats_block_shadow,
  });
}

/**
 * Converte o formato do front para o payload do banco.
 */
function mapHeroToRow(hero: Partial<HeroConfig>) {
  return {
    hero_id: hero.id,
    is_active: hero.isActive,
    badge: hero.badge,
    title: hero.title,
    subtitle: hero.subtitle,
    background_image: hero.backgroundImage,
    buttons: hero.buttons,
    stats: hero.stats,
    updated_at: new Date().toISOString(),

    hero_background_mode: hero.hero_background_mode,

    hero_background_color: hero.hero_background_color,
    hero_background_opacity: hero.hero_background_opacity,
    hero_background_gradient: hero.hero_background_gradient,

    hero_solid_color: hero.hero_solid_color,

    hero_overlay_color: hero.hero_overlay_color,
    hero_overlay_opacity: hero.hero_overlay_opacity,

    hero_gradient_from: hero.hero_gradient_from,
    hero_gradient_to: hero.hero_gradient_to,
    hero_gradient_direction: hero.hero_gradient_direction,
    hero_gradient_opacity: hero.hero_gradient_opacity,

    badge_background_color: hero.badge_background_color,
    badge_text_color: hero.badge_text_color,
    badge_border_color: hero.badge_border_color,
    badge_shadow: hero.badge_shadow,

    button_primary_color: hero.button_primary_color,
    button_primary_text_color: hero.button_primary_text_color,
    button_primary_border_color: hero.button_primary_border_color,
    button_primary_hover: hero.button_primary_hover,
    button_primary_gradient: hero.button_primary_gradient,

    button_secondary_color: hero.button_secondary_color,
    button_secondary_text_color: hero.button_secondary_text_color,
    button_secondary_border_color: hero.button_secondary_border_color,
    button_secondary_hover: hero.button_secondary_hover,
    button_secondary_gradient: hero.button_secondary_gradient,

    stats_block_background: hero.stats_block_background,
    stats_block_opacity: hero.stats_block_opacity,
    stats_block_border: hero.stats_block_border,
    stats_block_blur: hero.stats_block_blur,
    stats_block_text_color: hero.stats_block_text_color,
    stats_block_icon_color: hero.stats_block_icon_color,
    stats_block_shadow: hero.stats_block_shadow,
  };
}

export function useHeroManager() {
  const [heroes, setHeroes] = useState<HeroConfig[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHeroes();
  }, []);

  const loadHeroes = async () => {
    setLoading(true);

    try {
      const { data, error } = await supabase
        .from('ibx_heroes')
        .select('*')
        .order('hero_id', { ascending: true });

      if (error) throw error;

      if (data && data.length > 0) {
        setHeroes(data.map(mapRowToHero));
      } else {
        await seedDefaultHeroes();
      }
    } catch (err) {
      console.error('Erro ao carregar heroes do Supabase:', err);
      setHeroes(DEFAULT_HEROES.map((hero) => withVisualDefaults(hero)));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Popula o banco com os heroes padrão já compatíveis com os novos campos.
   */
  const seedDefaultHeroes = async () => {
    try {
      const rows = DEFAULT_HEROES.map((hero) =>
        mapHeroToRow(withVisualDefaults(hero))
      );

      const { error } = await supabase.from('ibx_heroes').insert(rows);
      if (error) throw error;

      setHeroes(DEFAULT_HEROES.map((hero) => withVisualDefaults(hero)));
    } catch (err) {
      console.error('Erro ao popular heroes no Supabase:', err);
      setHeroes(DEFAULT_HEROES.map((hero) => withVisualDefaults(hero)));
    }
  };

  const getActiveHero = (): HeroConfig | null => {
    return (
      heroes.find((hero) => hero.isActive) ||
      heroes[0] ||
      withVisualDefaults(DEFAULT_HEROES[0])
    );
  };

  const activateHero = async (heroId: string): Promise<boolean> => {
    try {
      const { error: deactivateError } = await supabase
        .from('ibx_heroes')
        .update({ is_active: false })
        .neq('hero_id', '');

      if (deactivateError) throw deactivateError;

      const { error: activateError } = await supabase
        .from('ibx_heroes')
        .update({ is_active: true, updated_at: new Date().toISOString() })
        .eq('hero_id', heroId);

      if (activateError) throw activateError;

      setHeroes((prev) =>
        prev.map((hero) => ({ ...hero, isActive: hero.id === heroId }))
      );

      return true;
    } catch (err) {
      console.error('Erro ao ativar hero:', err);
      return false;
    }
  };

  /**
   * Atualiza conteúdo + estilo visual sem quebrar a persistência atual.
   */
  const updateHero = async (
    heroId: string,
    updates: Partial<HeroConfig>
  ): Promise<boolean> => {
    try {
      const normalizedUpdates = withVisualDefaults(updates);
      const rowPayload = mapHeroToRow(normalizedUpdates);
      const { hero_id, is_active, ...updatePayload } = rowPayload;

      const { error } = await supabase
        .from('ibx_heroes')
        .update(updatePayload)
        .eq('hero_id', heroId);

      if (error) throw error;

      setHeroes((prev) =>
        prev.map((hero) =>
          hero.id === heroId
            ? withVisualDefaults({
                ...hero,
                ...updates,
                updatedAt: new Date().toISOString(),
              })
            : hero
        )
      );

      return true;
    } catch (err) {
      console.error('Erro ao atualizar hero:', err);
      return false;
    }
  };

  const getHeroById = (heroId: string): HeroConfig | undefined => {
    return heroes.find((hero) => hero.id === heroId);
  };

  const getAllHeroes = (): HeroConfig[] => heroes;

  const resetToDefaults = async (): Promise<boolean> => {
    return activateHero('hero-1-original');
  };

  const forceActivatePrimary = async (): Promise<boolean> => {
    return activateHero('hero-1-original');
  };

  return {
    heroes,
    loading,
    getActiveHero,
    activateHero,
    updateHero,
    getHeroById,
    getAllHeroes,
    resetToDefaults,
    forceActivatePrimary,
    refresh: loadHeroes,
  };
}